import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Field, Form } from 'react-final-form';
import {
	Button,
	FormControlLabel,
	Grid,
	MenuItem,
	Radio,
	Select,
	FormHelperText,
	InputLabel,
	FormControl,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import TextField from '../shared/TextField';
import { OnChange } from 'react-final-form-listeners';

const styles = (_) => ({
	paper: {
		width: '600px',
	},
	form: {
		padding: '15px 15px 15px 15px',
		'& input': {
			width: '550px',
		},
	},
	button: {
		float: 'right',
		marginLeft: '10px',
	},
	formControl: {
		minWidth: '120px',
	},
	select: {
		minWidth: '200px',
	},
});

const radioWrapper = ({
	input: { checked, value, name, onChange, ...restInput },
	meta,
	...rest
}) => (
	<Radio
		{...rest}
		name={name}
		inputProps={restInput}
		onChange={onChange}
		checked={checked}
		value={value}
	/>
);

const selectWrapper = ({
	input: { name, value, onChange, ...restInput },
	meta,
	label,
	formControlProps,
	...rest
}) => {
	const showError =
		((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched;

	return (
		<FormControl {...formControlProps} error={showError}>
			<InputLabel htmlFor={name}>{label}</InputLabel>

			<Select
				{...rest}
				name={name}
				onChange={onChange}
				inputProps={restInput}
				value={value}
			/>

			{showError && <FormHelperText>{meta.error || meta.submitError}</FormHelperText>}
		</FormControl>
	);
};

const required = (value) => (value ? undefined : 'Required');

const numRequired = (value) => (Number.isFinite(value) ? undefined : 'Required');

const isValidFormat = (key) =>
	/^[\d]{6}$/.test(key) ? undefined : 'A valid, 6 digit company key is required';

const isUnique = (value, keys) => (!keys.includes(value) ? undefined : 'Key must be unique');

const validateForm = ({ Environment, BrandingId }) => {
	if (!Environment) {
		return { Environment: 'Required' };
	}
	if (!BrandingId) {
		return 'Default Values are Required';
	}
};

const defaultValues = {
	IsTestDatabase: 'false',
	UserLicenses: 1,
	MobileLicenses: 0,
	CompanyKey: '',
};

const CreateTenant = ({
	allKeys,
	history,
	companyKey,
	envs,
	classes,
	onCreateTenant,
	getBrandings,
	getUniqueCompanyKey,
}) => {
	const handleCancel = () => history.push('/tenants');
	const [brandings, setBrandings] = useState([]);
	const formRef = useRef(null);

	useEffect(() => {
		formRef.current.change('CompanyKey', companyKey.toString());
	}, [companyKey]);

	const handleSelectEnvironment = async (e) => {
		setBrandings(await getBrandings(e));
	};

	const handleSubmit = async (values) => {
		await onCreateTenant(values);
		history.push('/tenants');
	};

	useEffect(() => {
		getUniqueCompanyKey();
	}, []);

	const validateCompanyKey = (value = '') =>
		required(value) || isValidFormat(value) || isUnique(value, allKeys);

	const exists = brandings.some((v) => v.brandingId === 1);

	const prefillUrls = useCallback(() => {
		formRef.current.change('BrandingId', brandings.length > 0 ? (exists ? 1 : '') : '');
	}, [formRef]);

	useEffect(() => {
		prefillUrls();
	}, [prefillUrls]);

	return (
		<Form
			onSubmit={handleSubmit}
			validate={validateForm}
			initialValues={defaultValues}
			render={({ handleSubmit, invalid, pristine, form }) => {
				formRef.current = form;

				return (
					<form onSubmit={handleSubmit} autoComplete="off">
						<Grid item xs={12}>
							<Field
								name="CompanyKey"
								label="Company Key"
								component={TextField}
								validate={validateCompanyKey}
								maxLength={6}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="CompanyName"
								label="Company Name"
								component={TextField}
								validate={required}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="AdminWorkwaveAccount"
								label="Admin WW Account Email"
								component={TextField}
								validate={required}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="AdminPassword"
								label="Admin Password"
								component={TextField}
								validate={required}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field name="Address" label="Address" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field name="Address2" label="Line 2" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field name="City" label="City" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field name="State" label="State" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field name="Zip" label="Zip" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field name="Phone" label="Phone" component={TextField} />
						</Grid>

						<Grid item xs={12}>
							<Field
								name="UserLicenses"
								label="# of Users"
								component={TextField}
								type="number"
								InputProps={{ inputProps: { min: 0 } }}
								parse={(v) => parseInt(v)}
								validate={numRequired}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="MobileLicenses"
								label="# of Mobile"
								component={TextField}
								type="number"
								InputProps={{ inputProps: { min: 0 } }}
								parse={(v) => parseInt(v)}
								validate={numRequired}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="ExpirationDate"
								label="Expiration Date"
								component={TextField}
								type="date"
								InputLabelProps={{
									shrink: true,
								}}
							/>
						</Grid>

						<Grid item xs={12}>
							<Field
								name="Environment"
								label="Environment *"
								component={selectWrapper}
								className={classes.select}
								required
							>
								{envs.map((e) => (
									<MenuItem key={e.Name} value={e.Name}>
										{e.Name}
									</MenuItem>
								))}
								<OnChange name="Environment">
									{(value, previous) => {
										handleSelectEnvironment(value);
									}}
								</OnChange>
							</Field>
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel
								label="Live"
								control={
									<Field
										name="IsTestDatabase"
										component={radioWrapper}
										type="radio"
										value="false"
									/>
								}
							/>
							<FormControlLabel
								label="Test"
								control={
									<Field
										name="IsTestDatabase"
										component={radioWrapper}
										type="radio"
										value="true"
									/>
								}
							/>
						</Grid>
						<Grid item xs={3}>
							<Field
								name="BrandingId"
								label="Default Values *"
								component={selectWrapper}
								className={classes.select}
								required
							>
								{brandings?.map((e) => (
									<MenuItem key={e.description} value={e.brandingId}>
										{e.description}
									</MenuItem>
								))}
							</Field>
						</Grid>
						<Grid item xs={12}>
							<Button
								variant="contained"
								color="primary"
								type="submit"
								className={classes.button}
								disabled={pristine || invalid}
							>
								Create Database
							</Button>
							<Button
								color="secondary"
								type="button"
								className={classes.button}
								onClick={handleCancel}
							>
								Cancel
							</Button>
						</Grid>
					</form>
				);
			}}
		/>
	);
};

export default withStyles(styles)(CreateTenant);
